import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Mail, MapPin, Phone, Send, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { submitContact, fetchCompany } from "@/lib/api";

export const ContactSection = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm();
  const [submitted, setSubmitted] = useState(false);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    fetchCompany().then(setCompany).catch(() => setCompany(null));
  }, []);

  const phone = company?.contact_phone || "+918708159671";
  const phoneDisplay = phone.replace(/(\+\d{2})(\d{5})(\d{5})/, "$1 $2 $3");

  const onSubmit = async (data) => {
    try {
      await submitContact(data);
      toast.success("Message sent! We'll respond shortly.");
      setSubmitted(true);
      reset();
      setTimeout(() => setSubmitted(false), 4500);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail?.[0]?.msg ||
          "Could not send message. Please try again."
      );
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 sm:py-28 bg-background"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <p className="text-sm font-semibold tracking-widest uppercase text-primary">
            Get in touch
          </p>
          <h2 className="mt-3 font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-foreground leading-tight">
            Talk to our
            <span className="gradient-text-blue"> Pediarange </span>
            team.
          </h2>
          <p className="mt-5 text-base text-muted-foreground leading-relaxed">
            Whether you're a paediatrician, retailer, distributor or medical
            representative — reach out and we'll get back within one business day.
          </p>

          <div className="mt-8 space-y-3">
            <a
              href="mailto:ayushayush921@gmail.com"
              data-testid="contact-email-link"
              className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-border soft-shadow hover:border-primary transition group"
            >
              <span className="w-12 h-12 rounded-2xl bg-primary text-primary-foreground grid place-items-center shrink-0">
                <Mail className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Email
                </p>
                <p className="font-medium text-foreground group-hover:text-primary transition truncate">
                  ayushayush921@gmail.com
                </p>
              </div>
            </a>

            <a
              href={`tel:${phone}`}
              data-testid="contact-phone-link"
              className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-border soft-shadow hover:border-primary transition group"
            >
              <span className="w-12 h-12 rounded-2xl bg-accent text-accent-foreground grid place-items-center shrink-0">
                <Phone className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Phone / WhatsApp
                </p>
                <p
                  className="font-semibold text-foreground group-hover:text-primary transition"
                  data-testid="contact-phone-display"
                >
                  {phoneDisplay}
                </p>
              </div>
            </a>

            <div className="flex items-center gap-4 p-5 rounded-2xl bg-white border border-border">
              <span className="w-12 h-12 rounded-2xl bg-secondary text-primary grid place-items-center shrink-0">
                <MapPin className="w-5 h-5" />
              </span>
              <div className="min-w-0">
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Office
                </p>
                <p className="font-medium text-foreground">
                  Greater Noida, Uttar Pradesh — 201308
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Mon – Sat · 10:00 to 19:00 IST
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form
            onSubmit={handleSubmit(onSubmit)}
            data-testid="contact-form"
            className="bg-white rounded-3xl border border-border p-6 sm:p-8 soft-shadow space-y-5"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="name" className="text-sm font-semibold">
                  Full name *
                </Label>
                <Input
                  id="name"
                  data-testid="contact-input-name"
                  className="mt-2 h-11 rounded-xl"
                  placeholder="Dr. A. Sharma"
                  {...register("name", { required: "Name is required" })}
                />
                {errors.name && (
                  <p className="text-xs text-destructive mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="email" className="text-sm font-semibold">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  data-testid="contact-input-email"
                  className="mt-2 h-11 rounded-xl"
                  placeholder="you@clinic.com"
                  {...register("email", {
                    required: "Email is required",
                    pattern: {
                      value: /^\S+@\S+\.\S+$/,
                      message: "Enter a valid email",
                    },
                  })}
                />
                {errors.email && (
                  <p className="text-xs text-destructive mt-1">
                    {errors.email.message}
                  </p>
                )}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label htmlFor="phone" className="text-sm font-semibold">
                  Phone
                </Label>
                <Input
                  id="phone"
                  data-testid="contact-input-phone"
                  className="mt-2 h-11 rounded-xl"
                  placeholder="+91 ..."
                  {...register("phone")}
                />
              </div>
              <div>
                <Label htmlFor="role" className="text-sm font-semibold">
                  Role
                </Label>
                <Input
                  id="role"
                  data-testid="contact-input-role"
                  className="mt-2 h-11 rounded-xl"
                  placeholder="Pediatrician / MR / Retailer"
                  {...register("role")}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="organization" className="text-sm font-semibold">
                Organization
              </Label>
              <Input
                id="organization"
                data-testid="contact-input-organization"
                className="mt-2 h-11 rounded-xl"
                placeholder="Hospital / Pharmacy / Distribution"
                {...register("organization")}
              />
            </div>

            <div>
              <Label htmlFor="message" className="text-sm font-semibold">
                Message *
              </Label>
              <Textarea
                id="message"
                data-testid="contact-input-message"
                rows={5}
                className="mt-2 rounded-xl"
                placeholder="Tell us how we can help…"
                {...register("message", {
                  required: "Please type a short message",
                  minLength: { value: 5, message: "At least 5 characters" },
                })}
              />
              {errors.message && (
                <p className="text-xs text-destructive mt-1">
                  {errors.message.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              data-testid="contact-submit-btn"
              className="rounded-full bg-primary hover:bg-primary/90 h-12 px-8 font-semibold"
            >
              {submitted ? (
                <>
                  <CheckCircle2 className="mr-2 w-4 h-4" /> Sent
                </>
              ) : isSubmitting ? (
                "Sending…"
              ) : (
                <>
                  Send message <Send className="ml-2 w-4 h-4" />
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
